/**
 * Session compound tool — start, close, status.
 * Consolidates agent-start, agent-close, agent-status into one tool.
 * Chain: PB-TEN-001 (AI starts from zero every session)
 */

import type { GatewayRouteReturnByName } from "@productbrain/kernel-client";
import { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import { z } from "zod/v3";
import {
  getWorkspaceContext,
  startAgentSession,
  closeAgentSession,
  getAgentSessionId,
  isSessionOriented,
  kernelCall,
  runWithToolContext,
} from "../client.js";
import { runWrapupReview, createWrapupHandler } from "./wrapup.js";
import { handleResume, handleCommitConstellation, facilitateSchema } from "./facilitate.js";
import { buildRitualsSurfaceLines, type RitualsSurfacePayload } from "./orient-rituals.js";
import { thinWrapper, success, failureResult, parseOrFail, type ToolResult } from "../envelope.js";
import { noticesToNextActions, formatNoticeLines, formatAdoptDiscoveryLine, FEEDBACK_SUBMIT_HINT } from "../lib/sessionNotices.js";
import { clearSessionGaps } from "../gap-store.js";
import { trackSessionCaptureRate } from "../analytics.js";
import { dispatchDiscriminated, type ActionParamSpec } from "../compound-tool.js";

// WP-484 S2 (§3): `session` absorbs `session-wrapup` → wrapup-review/wrapup-commit
// and `facilitate` → resume/commit-constellation. Legacy tools stay registered
// alongside (§7 "alongside" mechanic) — both call the SAME handler functions
// (wrapupHandler from wrapup.ts, handleResume/handleCommitConstellation from
// facilitate.ts), one business-logic path per action.
const SESSION_ACTIONS = [
  "start", "close", "status", "wrapup-review", "wrapup-commit", "resume", "commit-constellation",
] as const;
type SessionAction = (typeof SESSION_ACTIONS)[number];

const coherencyAcknowledgementFlatSchema = z.object({
  response: z.string().max(200).describe("Acknowledgement response per offender: 'linked' | 'accepted-fix' | 'diverged'."),
  entryId: z.string().max(200).optional().describe("Entry the acknowledgement links to."),
  reason: z.string().max(2000).optional().describe("Free-text justification, required for a 'diverged' response."),
  subjectEntryId: z.string().max(200).optional().describe("The refused entry this acknowledgement answers."),
});

// §3a — the REGISTERED layer: flat ZodObject, action enum + superset of params.
export const sessionSchema = z.object({
  action: z.enum(SESSION_ACTIONS).describe(
    "'start': begin a tracked session. 'close': end the session and record activity. 'status': check current session state. " +
      "'wrapup-review': review uncommitted drafts before closing (absorbs session-wrapup action=review). " +
      "'wrapup-commit': accept all uncommitted drafts (absorbs session-wrapup action=commit-all). " +
      "'resume': load session state from an existing bet entry (absorbs facilitate action=resume). " +
      "'commit-constellation': atomically accept a bet and its linked drafts (absorbs facilitate action=commit-constellation).",
  ),
  betEntryId: z.string().max(200).optional().describe("For 'resume'/'commit-constellation': bet entry ID. Required for both."),
  operationId: z.string().max(200).optional().describe("For 'commit-constellation': optional idempotency key for retries."),
  coherencyAcknowledgements: z.array(coherencyAcknowledgementFlatSchema).max(20).optional().describe(
    "For 'commit-constellation': per-offender acknowledgements to retry a constellation held under standard/strict coherency mode.",
  ),
  steeringOverrideReason: z.string().max(2000).optional().describe(
    "For 'commit-constellation': typed override reason (>= 12 chars) to push past a coherency hold instead of acknowledging.",
  ),
});

// §3b — the HANDLER layer: per-action discriminatedUnion, reusing existing
// single-purpose schemas (Code Integrity — facilitateSchema already knows the
// resume/commit-constellation shape; `.extend()` adds only the discriminant).
const sessionStartVariant = z.object({ action: z.literal("start") });
const sessionCloseVariant = z.object({ action: z.literal("close") });
const sessionStatusVariant = z.object({ action: z.literal("status") });
const sessionWrapupReviewVariant = z.object({ action: z.literal("wrapup-review") });
const sessionWrapupCommitVariant = z.object({ action: z.literal("wrapup-commit") });
const sessionResumeVariant = facilitateSchema.omit({ action: true }).extend({ action: z.literal("resume") });
const sessionCommitConstellationVariant = facilitateSchema.omit({ action: true }).extend({ action: z.literal("commit-constellation") });

export const sessionActionUnion = z.discriminatedUnion("action", [
  sessionStartVariant,
  sessionCloseVariant,
  sessionStatusVariant,
  sessionWrapupReviewVariant,
  sessionWrapupCommitVariant,
  sessionResumeVariant,
  sessionCommitConstellationVariant,
]);

/** §4 backpressure text — one entry per action, never used for registration. */
export const SESSION_ACTION_SPECS: Record<SessionAction, ActionParamSpec> = {
  start: { params: [], description: "Begin a tracked session; no params." },
  close: { params: [], description: "End the current session; no params." },
  status: { params: [], description: "Check current session state; no params." },
  "wrapup-review": { params: [], description: "Review uncommitted drafts; no params." },
  "wrapup-commit": { params: [], description: "Accept all uncommitted drafts; no params." },
  resume: { params: ["betEntryId"], description: "betEntryId is required." },
  "commit-constellation": {
    params: ["betEntryId", "operationId", "coherencyAcknowledgements", "steeringOverrideReason"],
    description: "betEntryId is required; every other field is optional.",
  },
};

export function registerSessionTools(server: McpServer): void {
  // Own review-cache instance (createWrapupHandler factory) — independent of
  // the legacy `session-wrapup` tool's cache; see wrapup.ts's docstring.
  const wrapupHandler = createWrapupHandler();

  // §3b handler map — one entry per action, dispatched via dispatchDiscriminated.
  const sessionHandlers: Record<SessionAction, (data: any) => Promise<ToolResult>> = {
    start: () => handleStart(),
    close: () => handleClose(),
    status: () => handleStatus(),
    "wrapup-review": () => wrapupHandler({ action: "review" }),
    "wrapup-commit": () => wrapupHandler({ action: "commit-all" }),
    resume: (data) => handleResume(data),
    "commit-constellation": (data) => handleCommitConstellation(data),
  };

  server.registerTool(
    "session",
    {
      title: "Session — Lifecycle & Facilitation",
      description:
        "Session lifecycle, wrapup review, and work package facilitation.\n\n" +
        "- **start**: Begin a tracked write session for attribution. **This is the ONLY call that creates a session** — " +
        "write tools (capture, entries action=update/commit, relations) require it. `orient action=start` orients an " +
        "existing session and loads stage-aware context, but does NOT create one: call `session action=start` first, " +
        "then `orient action=start`.\n" +
        "- **close**: End the current session. Records structured data (entries created, modified, relations). " +
        "Run `session action=wrapup-review` before closing.\n" +
        "- **status**: Check current session — active or not, oriented, activity counts.\n" +
        "- **wrapup-review**: Review uncommitted drafts with quality scores, suggested links. Run before close.\n" +
        "- **wrapup-commit**: Accept all uncommitted drafts and create suggested links.\n" +
        "- **resume**: Load session state (work package data, constellation, drafts) from an existing work package entry.\n" +
        "- **commit-constellation**: Atomically accept a work package and all its linked draft entries in one call.",
      inputSchema: sessionSchema,
      // Mixed read/write noun (§3 R3): wrapup-commit/resume/commit-constellation write.
      annotations: { readOnlyHint: false, destructiveHint: false, openWorldHint: false },
    },
    thinWrapper(async (args) => {
      const parsed = parseOrFail(sessionSchema, args);
      if (!parsed.ok) return parsed.result;

      const { action } = parsed.data;

      return runWithToolContext({ tool: "session", action }, () =>
        dispatchDiscriminated("session", sessionActionUnion, parsed.data, SESSION_ACTION_SPECS, sessionHandlers),
      );
    }),
  );
}

async function handleStart() {
  const result = await startAgentSession();

  const lines: string[] = [];

  lines.push(
    `Session ${result.sessionId} active. ` +
      `Initiated by ${result.initiatedBy}. ` +
      `Workspace ${result.workspaceName}. ` +
      `Scope: ${result.toolsScope}. ` +
      `Write tools available after orient.`,
  );

  // WP-584 S4 (FEAT-1412 / TEN-2971): the legacy plain-text "Feedback queue: N new..." line
  // used to render here — skipped by consuming agents (dogfood 2026-08-02). It is now the
  // `feedback-queue` spine notice (until-resolved, standing state), rendered structurally
  // through the SAME `notices` block below every other spine notice already uses. The raw
  // `feedbackQueueNew`/`feedbackOldestNewAt` fields stay on `structuredContent` (S1 contract
  // item 7, published clients) — only the bespoke rendered line is gone.
  lines.push(FEEDBACK_SUBMIT_HINT);

  // FEAT-1409 spine core: low-severity notices never take the headline (HARD CONSTRAINT 14) —
  // appended below the session banner, never a top-of-output interrupt.
  const noticeLines = formatNoticeLines(result.notices);
  if (noticeLines.length > 0) {
    lines.push("", "Notices:", ...noticeLines);
  }

  // WP-638 S4 (§10.3) — the bounded adopt-discovery advisory: silent at zero (the server omits
  // the field entirely rather than sending a "0" line).
  const adoptLines = formatAdoptDiscoveryLine(result.adoptDiscoveryHint);
  if (adoptLines.length > 0) {
    lines.push("", ...adoptLines);
  }

  const summary = `Session ${result.sessionId} active. Workspace ${result.workspaceName}. Write tools available after orient.`;
  return {
    content: [{ type: "text" as const, text: lines.join("\n") }],
    structuredContent: success(summary, {
      sessionId: result.sessionId,
      initiatedBy: result.initiatedBy,
      workspaceName: result.workspaceName,
      toolsScope: result.toolsScope,
      feedbackQueueNew: result.feedbackQueueNew,
      feedbackOldestNewAt: result.feedbackOldestNewAt,
      notices: result.notices ?? [],
      adoptDiscoveryHint: result.adoptDiscoveryHint,
    // Finding #16: bare `parameters: {}` lets orient's schema default kick in
    // (action defaults to "task", which requires a `task` string) — the suggested
    // next-action call failed validation instead of running. `action: "start"` is
    // the guided-setup entrypoint this session just opened write access for.
    // FEAT-1409 spine core: notice next-actions are appended through this SAME existing
    // `next` field (HARD CONSTRAINT 2) — never a parallel action list.
    }, [{ tool: "orient", description: "Orient session", parameters: { action: "start" } }, ...noticesToNextActions(result.notices)]),
  };
}

async function handleClose() {
  const sessionId = getAgentSessionId();
  if (!sessionId) {
    return failureResult(
      "No active agent session to close.",
      "SESSION_REQUIRED",
      "No active agent session to close.",
      "Start a session first.",
      [{ tool: "session", description: "Start a session", parameters: { action: "start" } }],
    );
  }

  const session = await kernelCall<GatewayRouteReturnByName<"agent.getSession">>("agent.getSession", {
    sessionId,
  });

  let wrapupNudge = "";
  if (session && !(session.wrapupCompletedAt as number)) {
    try {
      const { text, data } = await runWrapupReview();
      if (data && data.drafts.length > 0) {
        wrapupNudge = [
          "## Session Wrapup",
          "",
          text,
          "",
          `> **${data.drafts.length} uncommitted draft(s) remain.** ` +
            "Run `session action=wrapup-commit` before closing next time.",
          "",
          "---",
          "",
        ].join("\n");
      }
    } catch {
      // Non-critical — proceed with close even if wrapup review fails
    }
  }

  await closeAgentSession();
  clearSessionGaps();

  // WP-306 S3: Emit capture rate event for PostHog measurement (STD-155)
  const created = (session?.entriesCreated as string[])?.length ?? 0;
  const modified = (session?.entriesModified as string[])?.length ?? 0;
  const relations = (session?.relationsCreated as number) ?? 0;

  const wsCtx = await getWorkspaceContext();
  if (wsCtx) {
    trackSessionCaptureRate(wsCtx.workspaceId, {
      entries_created: created,
      entries_modified: modified,
      relations_created: relations,
      had_captures: created > 0,
    });
  }

  const lines: string[] = [
    `Session ${sessionId} closed.`,
    "",
  ];
  const gates = (session?.gateFailures as number) ?? 0;
  const warnings = (session?.contradictionWarnings as number) ?? 0;

  if (session) {
    lines.push(
      `| Metric | Count |`,
      `|--------|-------|`,
      `| Entries created | ${created} |`,
      `| Entries modified | ${modified} |`,
      `| Relations created | ${relations} |`,
      `| Gate failures | ${gates} |`,
      `| Contradiction warnings | ${warnings} |`,
    );
  }

  // WP-582 B2 round 1 (Codex P2 review, PR #532, T10): the same rituals surface orient renders,
  // fetched via the lightweight `chain.getRitualsSurface` route (workspaceId injected from the
  // bearer credential — never passed here). Best-effort: a fetch failure omits the block rather
  // than blocking close or rendering the no-row fallback (that fallback is for a genuine no-row
  // arm, not a read we chose not to attempt) — mirrors the CLI's identical posture
  // (commands/session.ts).
  let rituals: RitualsSurfacePayload | null = null;
  try {
    rituals = await kernelCall<GatewayRouteReturnByName<"chain.getRitualsSurface">>("chain.getRitualsSurface", {});
  } catch {
    // Non-critical — the close output simply omits the rituals block.
  }
  if (rituals) lines.push(...buildRitualsSurfaceLines(rituals));

  lines.push("", "Write tools are now blocked. Session data saved for future orientation.");

  const fullResponse = wrapupNudge + lines.join("\n");
  const summary = `Session ${sessionId} closed. ${created} created, ${modified} modified.`;
  return {
    content: [{ type: "text" as const, text: fullResponse }],
    structuredContent: success(summary, {
      sessionId,
      entriesCreated: created,
      entriesModified: modified,
      relationsCreated: relations,
      gateFailures: gates,
      contradictionWarnings: warnings,
      wrapupNudge: wrapupNudge.length > 0,
      // T1's undefined-vs-null distinction applies here too — omit the key entirely when the
      // fetch failed, rather than forcing a null a machine consumer can't distinguish from a
      // genuine no-row arm.
      ...(rituals?.directionLine !== undefined ? { directionLine: rituals.directionLine } : {}),
      ...(rituals?.verdictAskLine !== undefined ? { verdictAskLine: rituals.verdictAskLine } : {}),
      ...(rituals?.spineLine !== undefined ? { spineLine: rituals.spineLine } : {}),
      ...(rituals?.atCapLine !== undefined ? { atCapLine: rituals.atCapLine } : {}), // D1 residue fix (named item c)
    }),
  };
}

async function handleStatus() {
  const sessionId = getAgentSessionId();
  if (!sessionId) {
    return failureResult(
      "No active agent session. Call session action=start to begin.",
      "SESSION_REQUIRED",
      "No active agent session.",
      "Call session action=start to begin.",
      [{ tool: "session", description: "Start a session", parameters: { action: "start" } }],
    );
  }

  const session = await kernelCall<GatewayRouteReturnByName<"agent.getSession">>("agent.getSession", {
    sessionId,
  });

  if (!session) {
    return failureResult(
      "Session not found in Convex. Call session action=start to create a new session.",
      "NOT_FOUND",
      "Session not found in Convex.",
      "Call session action=start to create a new session.",
      [{ tool: "session", description: "Start a session", parameters: { action: "start" } }],
    );
  }

  const oriented = isSessionOriented();
  const created = (session.entriesCreated as string[])?.length ?? 0;
  const modified = (session.entriesModified as string[])?.length ?? 0;

  const lines = [
    `Session ${sessionId} ${session.status}.`,
    `Initiated by ${session.initiatedBy}.`,
    `Scope: ${session.toolsScope}.`,
    `Oriented: ${oriented ? "yes" : "no"}.`,
    "",
    `| Metric | Value |`,
    `|--------|-------|`,
    `| Entries created | ${created} |`,
    `| Entries modified | ${modified} |`,
    `| Relations created | ${session.relationsCreated ?? 0} |`,
    `| Started | ${new Date(session.startedAt as number).toISOString()} |`,
    `| Expires | ${new Date(session.expiresAt as number).toISOString()} |`,
  ];

  const summary = `Session ${sessionId} ${session.status}. ${created} created, ${modified} modified.`;
  return {
    content: [{ type: "text" as const, text: lines.join("\n") }],
    structuredContent: success(summary, {
      sessionId,
      status: session.status,
      initiatedBy: session.initiatedBy,
      toolsScope: session.toolsScope,
      oriented,
      entriesCreated: created,
      entriesModified: modified,
      relationsCreated: session.relationsCreated ?? 0,
    }),
  };
}
